import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchLinkInfoByCode,
  fetchLinkInfoBySlug,
  fetchLinkInfoById,
  LinkNotFoundError,
  LinkOwnershipChangedError,
} from "../api/paymentLinksApi";

/**
 * Load a payment link's public /info metadata.
 *
 * Accepts one of three route-param shapes and picks the matching
 * fetcher. Caches in a ref so re-renders don't re-hit the network;
 * a route change (different params) re-fetches.
 *
 * Returns a discriminated `{ status, link?, error? }` so the page
 * renders cleanly:
 *   - "loading"   → skeleton
 *   - "ok"        → PaymentPage with link
 *   - "not-found" → NotFoundPage (404)
 *   - "gone"      → rename-pointer / expired / disabled message
 *   - "error"     → retry UI
 *
 * We avoid pulling in @tanstack/react-query here — wagmi 1.x
 * doesn't use it, and the whole payment flow needs exactly one
 * cache entry per mounted page. A bespoke cache keeps bundle size
 * flat.
 */
export function usePaymentLink(params) {
  const [state, setState] = useState({ status: "loading" });
  // Cache key = serialized params so a navigation from /pay/p/ABC
  // to /pay/p/DEF triggers a refetch but a re-render with the
  // same params doesn't.
  const cacheRef = useRef({ key: null, promise: null });

  const paramsKey = JSON.stringify(params || {});

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      let link;
      if (params?.type === "code" && params.code) {
        link = await fetchLinkInfoByCode(params.code);
      } else if (params?.type === "slug" && params.username && params.slug) {
        link = await fetchLinkInfoBySlug(params.username, params.slug);
      } else if (params?.type === "id" && params.linkId) {
        link = await fetchLinkInfoById(params.linkId);
      } else {
        // Unknown param shape — treat as a 404 rather than a 500.
        setState({
          status: "not-found",
          error: new LinkNotFoundError("Invalid payment link URL."),
        });
        return;
      }
      setState({ status: "ok", link });
    } catch (err) {
      if (err instanceof LinkNotFoundError) {
        setState({ status: "not-found", error: err });
      } else if (err instanceof LinkOwnershipChangedError) {
        // Rename / release / re-register — the backend wants the
        // page to redirect users to the new URL rather than
        // render a stale @username header.
        setState({ status: "gone", error: err, reason: "owner_username_changed" });
      } else if (err?.statusCode === 410) {
        setState({ status: "gone", error: err, reason: err.reason || "gone" });
      } else {
        setState({ status: "error", error: err });
      }
    }
  }, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cacheRef.current.key === paramsKey) return;
    cacheRef.current.key = paramsKey;
    load();
  }, [paramsKey, load]);

  return { ...state, refetch: load };
}
